import addonApi from '@/api/addons';

/**
 * Service for managing addons and their relationships with products and stores.
 */
class AddonService {
  /**
   * Get all addons
   * @returns {Promise} Promise that resolves to an array of addons
   */
  async getAllAddons() {
    try {
      const response = await addonApi.getAllAddons();
      return response.data;
    } catch (error) {
      console.error('Error fetching addons:', error);
      throw error;
    }
  }

  /**
   * Get an addon by ID
   * @param {number} id - The ID of the addon
   * @returns {Promise} Promise that resolves to the addon
   */
  async getAddonById(id) {
    try {
      const response = await addonApi.getAddonById(id);
      return response.data;
    } catch (error) {
      console.error(`Error fetching addon with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new addon
   * @param {Object} addon - The addon to create
   * @returns {Promise} Promise that resolves to the created addon
   */
  async createAddon(addon) {
    try {
      const response = await addonApi.createAddon(addon);
      return response.data;
    } catch (error) {
      console.error('Error creating addon:', error);
      throw error;
    }
  }

  /**
   * Update an existing addon
   * @param {number} id - The ID of the addon to update
   * @param {Object} addon - The updated addon data
   * @returns {Promise} Promise that resolves to the updated addon
   */
  async updateAddon(id, addon) {
    try {
      const response = await addonApi.updateAddon(id, addon);
      return response.data;
    } catch (error) {
      console.error(`Error updating addon with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete an addon
   * @param {number} id - The ID of the addon to delete
   * @returns {Promise} Promise that resolves when the addon is deleted
   */
  async deleteAddon(id) {
    try {
      const response = await addonApi.deleteAddon(id);
      return response.data;
    } catch (error) {
      console.error(`Error deleting addon with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get addons by product ID
   * @param {number} productId - The ID of the product
   * @returns {Promise} Promise that resolves to an array of addons
   */
  async getAddonsByProductId(productId) {
    try {
      const response = await addonApi.getAddonsByProductId(productId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching addons for product with ID ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Add an addon to a product
   * @param {number} productId - The ID of the product
   * @param {number} addonId - The ID of the addon
   * @returns {Promise} Promise that resolves when the addon is added to the product
   */
  async addAddonToProduct(productId, addonId) {
    try {
      const response = await addonApi.addAddonToProduct(productId, addonId);
      return response.data;
    } catch (error) {
      console.error(`Error adding addon ${addonId} to product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Remove an addon from a product
   * @param {number} productId - The ID of the product
   * @param {number} addonId - The ID of the addon
   * @returns {Promise} Promise that resolves when the addon is removed from the product
   */
  async removeAddonFromProduct(productId, addonId) {
    try {
      const response = await addonApi.removeAddonFromProduct(productId, addonId);
      return response.data;
    } catch (error) {
      console.error(`Error removing addon ${addonId} from product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get addons by store ID
   * @param {number} storeId - The ID of the store
   * @returns {Promise} Promise that resolves to an array of addons
   */
  async getAddonsByStoreId(storeId) {
    try {
      const response = await addonApi.getAddonsByStoreId(storeId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching addons for store with ID ${storeId}:`, error);
      throw error;
    }
  }

  /**
   * Add an addon to a store
   * @param {number} storeId - The ID of the store
   * @param {number} addonId - The ID of the addon
   * @returns {Promise} Promise that resolves when the addon is added to the store
   */
  async addAddonToStore(storeId, addonId) {
    try {
      const response = await addonApi.addAddonToStore(storeId, addonId);
      return response.data;
    } catch (error) {
      console.error(`Error adding addon ${addonId} to store ${storeId}:`, error);
      throw error;
    }
  }

  /**
   * Remove an addon from a store
   * @param {number} storeId - The ID of the store
   * @param {number} addonId - The ID of the addon
   * @returns {Promise} Promise that resolves when the addon is removed from the store
   */
  async removeAddonFromStore(storeId, addonId) {
    try {
      const response = await addonApi.removeAddonFromStore(storeId, addonId);
      return response.data;
    } catch (error) {
      console.error(`Error removing addon ${addonId} from store ${storeId}:`, error);
      throw error;
    }
  }

  /**
   * Get addons by product ID using query parameter
   * @param {number} productId - The ID of the product
   * @returns {Promise} Promise that resolves to an array of addons
   */
  async getAddonsByProductIdQuery(productId) {
    try {
      const response = await addonApi.getAddonsByProductIdQuery(productId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching addons for product with ID ${productId} using query:`, error);
      throw error;
    }
  }
}

export default new AddonService();